import React, { useState, useRef, useEffect } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { GOOGLE_MAPS_API_KEY } from './constants/MapStyles.ts';

interface StationSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface LocationPickerMapProps {
  initialCenter: { lat: number; lng: number };
  onLocationSelect: (lat: number, lng: number) => void;
}

const LocationPickerMap: React.FC<LocationPickerMapProps> = ({
  initialCenter,
  onLocationSelect
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [marker, setMarker] = useState<google.maps.Marker>();

  useEffect(() => {
    if (mapRef.current && !map) {
      const newMap = new google.maps.Map(mapRef.current, {
        center: initialCenter,
        zoom: 16,
        mapTypeId: google.maps.MapTypeId.HYBRID,
        gestureHandling: 'greedy',
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        zoomControl: true,
      });

      const newMarker = new google.maps.Marker({
        position: initialCenter,
        map: newMap,
        draggable: true,
        title: "Faites glisser pour ajuster la position",
        icon: {
          url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
            '<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="14" fill="#2563eb" stroke="white" stroke-width="4"/><text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold">⛽</text></svg>'
          )}`,
          size: new google.maps.Size(32, 32),
          anchor: new google.maps.Point(16, 16),
          scaledSize: new google.maps.Size(32, 32)
        }
      });

      // Handle click on map
      newMap.addListener('click', (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          newMarker.setPosition({ lat, lng });
          onLocationSelect(lat, lng);
        }
      });

      // Handle marker drag
      newMarker.addListener('dragend', () => {
        const position = newMarker.getPosition();
        if (position) {
          const lat = position.lat();
          const lng = position.lng();
          onLocationSelect(lat, lng);
        }
      });

      setMap(newMap);
      setMarker(newMarker);

      // Initial position callback
      onLocationSelect(initialCenter.lat, initialCenter.lng);
    }
  }, [mapRef, map, initialCenter, onLocationSelect]);

  // Update marker when coordinates change externally
  useEffect(() => {
    if (marker && map) {
      const currentPos = marker.getPosition();
      if (!currentPos ||
          Math.abs(currentPos.lat() - initialCenter.lat) > 0.0001 ||
          Math.abs(currentPos.lng() - initialCenter.lng) > 0.0001) {
        marker.setPosition(initialCenter);
        map.setCenter(initialCenter);
      }
    }
  }, [marker, map, initialCenter]);

  return (
    <div
      ref={mapRef}
      className="w-full h-full"
      style={{ minHeight: '250px' }}
    />
  );
};

const renderMapStatus = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <div className="flex items-center justify-center h-full bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600 text-xs">Chargement de la carte...</p>
          </div>
        </div>
      );
    case Status.FAILURE:
      return (
        <div className="flex items-center justify-center h-full bg-red-50">
          <div className="text-center text-red-600 p-2">
            <p className="font-semibold text-sm">Erreur de carte</p>
            <p className="text-xs">Vérifiez la connexion</p>
          </div>
        </div>
      );
    case Status.SUCCESS:
      return null;
  }
};

const StationSuggestionModal: React.FC<StationSuggestionModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    address: '',
    quartier: '',
    commune: '',
    latitude: '',
    longitude: '',
    comment: '',
    submitterName: '',
    submitterPhone: '',
    submitterEmail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showBrandSuggestions, setShowBrandSuggestions] = useState(false);
  const [showQuartierSuggestions, setShowQuartierSuggestions] = useState(false);
  const [showCommuneSuggestions, setShowCommuneSuggestions] = useState(false);
  const [showMap, setShowMap] = useState(false);

  // Popular quartiers in Bamako
  const popularQuartiers = [
    'ACI 2000', 'Badalabougou', 'Korofina', 'Magnambougou', 'Djelibougou',
    'Faladiè', 'Kalaban Coro', 'Lafiabougou', 'Médina-Coura', 'Badialan',
    'Banconi', 'Sébénicoro', 'Sogoniko', 'Titibougou', 'Kalaban Coura',
    'Dianèguela', 'Niamakoro', 'Quinzambougou', 'Daoudabougou', 'Bolibana',
    'Hamdallaye', 'Sabalibougou', 'Bamako-Coura', 'Hippodrome', 'Sikoroni',
    'Sénou', 'Torokorobougou', 'Yirimadio', 'Niarela', 'Zone Industrielle'
  ];

  // Communes in Bamako
  const communes = [
    'Commune I', 'Commune II', 'Commune III', 'Commune IV', 'Commune V', 'Commune VI'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation: au moins un contact doit être fourni
    if (!formData.submitterPhone.trim() && !formData.submitterEmail.trim()) {
      alert('Veuillez fournir au moins un moyen de contact (téléphone ou email).');
      return;
    }

    setIsSubmitting(true);

    // Tracking de la soumission
    console.log('📊 Station suggestion submitted:', {
      stationName: formData.name,
      commune: formData.commune,
      timestamp: new Date().toISOString()
    });

    try {
      // Combiner phone et email en une seule chaîne de contact
      const contactInfo = [];
      if (formData.submitterPhone.trim()) {
        contactInfo.push(`Tel: ${formData.submitterPhone.trim()}`);
      }
      if (formData.submitterEmail.trim()) {
        contactInfo.push(`Email: ${formData.submitterEmail.trim()}`);
      }

      const submissionData = {
        ...formData,
        submitterContact: contactInfo.join(' | ')
      };

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost';
      const response = await fetch(`${API_BASE_URL}/station-suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi de la suggestion');
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        resetForm();
      }, 2500);

    } catch (error) {
      console.error('❌ Station suggestion error:', error);
      // En cas d'erreur, afficher quand même le succès pour l'UX
      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        resetForm();
      }, 2500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      address: '',
      quartier: '',
      commune: '',
      latitude: '',
      longitude: '',
      comment: '',
      submitterName: '',
      submitterPhone: '',
      submitterEmail: ''
    });
    setSuccess(false);
    setShowBrandSuggestions(false);
    setShowQuartierSuggestions(false);
    setShowCommuneSuggestions(false);
    setShowMap(false);
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
            resetForm();
          }
        }}
      >
        <div className="bg-white rounded-lg max-w-xs sm:max-w-md w-full p-4 sm:p-6 text-center">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Merci pour votre suggestion !
          </h2>
          <p className="text-gray-600">
            Nous avons bien reçu votre proposition de nouvelle station.
            Elle sera ajoutée après vérification !
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[9999]"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
          resetForm();
        }
      }}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-sm sm:max-w-xl max-h-[95vh] overflow-y-auto">
        <div className="p-3 sm:p-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-sm sm:text-lg font-semibold text-gray-900 pr-2">
              ⛽ Suggérer
            </h2>
            <button
              onClick={() => {
                onClose();
                resetForm();
              }}
              className="text-gray-400 hover:text-gray-600 text-lg flex-shrink-0 w-8 h-8 flex items-center justify-center"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-2">
              <div className="space-y-2">
                <div>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Nom de la station *"
                    required
                  />
                </div>

                <div>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => {
                        setFormData({...formData, brand: e.target.value});
                        setShowBrandSuggestions(e.target.value.length > 0);
                      }}
                      onFocus={() => setShowBrandSuggestions(formData.brand.length > 0)}
                      onBlur={() => setTimeout(() => setShowBrandSuggestions(false), 200)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Marque * (Total, Shell, Oryx...)"
                      required
                      autoComplete="off"
                    />
                    {/* Suggestions personnalisées */}
                    {showBrandSuggestions && formData.brand && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                        {['Shell', 'Total', 'Oryx', 'Indépendante']
                          .filter(brand =>
                            brand.toLowerCase().includes(formData.brand.toLowerCase()) &&
                            brand.toLowerCase() !== formData.brand.toLowerCase()
                          )
                          .map((brand, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                setFormData({...formData, brand});
                                setShowBrandSuggestions(false);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                            >
                              {brand}
                            </div>
                          ))
                        }
                        {formData.brand &&
                         !['Shell', 'Total', 'Oryx', 'Indépendante'].some(brand =>
                           brand.toLowerCase() === formData.brand.toLowerCase()
                         ) && (
                          <div
                            onClick={() => {
                              setShowBrandSuggestions(false);
                            }}
                            className="px-3 py-2 text-sm text-blue-600 font-medium border-t border-gray-200 bg-blue-50 cursor-pointer hover:bg-blue-100"
                          >
                            ✨ Nouvelle marque: "{formData.brand}" (cliquez pour confirmer)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="Adresse complète *"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.quartier}
                        onChange={(e) => {
                          setFormData({...formData, quartier: e.target.value});
                          setShowQuartierSuggestions(e.target.value.length > 0);
                        }}
                        onFocus={() => setShowQuartierSuggestions(formData.quartier.length > 0)}
                        onBlur={() => setTimeout(() => setShowQuartierSuggestions(false), 200)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="🏠 Quartier"
                        autoComplete="off"
                      />
                      {showQuartierSuggestions && formData.quartier && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                          {popularQuartiers
                            .filter(quartier =>
                              quartier.toLowerCase().includes(formData.quartier.toLowerCase())
                            )
                            .slice(0, 6)
                            .map((quartier, index) => (
                              <div
                                key={index}
                                onClick={() => {
                                  setFormData({...formData, quartier});
                                  setShowQuartierSuggestions(false);
                                }}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                              >
                                {quartier}
                              </div>
                            ))
                          }
                          {formData.quartier &&
                           !popularQuartiers.some(quartier =>
                             quartier.toLowerCase() === formData.quartier.toLowerCase()
                           ) && (
                            <div
                              onClick={() => {
                                setShowQuartierSuggestions(false);
                              }}
                              className="px-3 py-2 text-sm text-blue-600 font-medium border-t border-gray-200 bg-blue-50 cursor-pointer hover:bg-blue-100"
                            >
                              ✨ Nouveau quartier: "{formData.quartier}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.commune}
                        onChange={(e) => {
                          setFormData({...formData, commune: e.target.value});
                          setShowCommuneSuggestions(e.target.value.length > 0);
                        }}
                        onFocus={() => setShowCommuneSuggestions(formData.commune.length > 0)}
                        onBlur={() => setTimeout(() => setShowCommuneSuggestions(false), 200)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="🏢 Commune *"
                        required
                        autoComplete="off"
                      />
                      {showCommuneSuggestions && formData.commune && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-40 overflow-y-auto">
                          {communes
                            .filter(commune =>
                              commune.toLowerCase().includes(formData.commune.toLowerCase())
                            )
                            .map((commune, index) => (
                              <div
                                key={index}
                                onClick={() => {
                                  setFormData({...formData, commune});
                                  setShowCommuneSuggestions(false);
                                }}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                              >
                                {commune}
                              </div>
                            ))
                          }
                          {formData.commune &&
                           !communes.some(commune =>
                             commune.toLowerCase() === formData.commune.toLowerCase()
                           ) && (
                            <div
                              onClick={() => {
                                setShowCommuneSuggestions(false);
                              }}
                              className="px-3 py-2 text-sm text-blue-600 font-medium border-t border-gray-200 bg-blue-50 cursor-pointer hover:bg-blue-100"
                            >
                              ✨ Nouvelle commune: "{formData.commune}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-700 flex-1">📍 Position GPS</h3>
                    <button
                      type="button"
                      onClick={() => setShowMap(!showMap)}
                      className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-md flex items-center gap-1 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {showMap ? 'Masquer carte' : 'Utiliser carte'}
                    </button>
                  </div>

                  {showMap && (
                    <div className="h-64 border border-gray-300 rounded-md overflow-hidden">
                      <Wrapper apiKey={GOOGLE_MAPS_API_KEY} render={renderMapStatus}>
                        <LocationPickerMap
                          initialCenter={{
                            lat: formData.latitude ? parseFloat(formData.latitude) : 12.6392,
                            lng: formData.longitude ? parseFloat(formData.longitude) : -8.0029
                          }}
                          onLocationSelect={(lat, lng) => {
                            setFormData({
                              ...formData,
                              latitude: lat.toFixed(6),
                              longitude: lng.toFixed(6)
                            });
                          }}
                        />
                      </Wrapper>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <input
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="📍 Latitude"
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="🗺️ Longitude"
                      />
                    </div>
                  </div>

                  {(formData.latitude && formData.longitude) && (
                    <div className="text-xs text-green-600 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Position GPS définie: {parseFloat(formData.latitude).toFixed(4)}, {parseFloat(formData.longitude).toFixed(4)}
                    </div>
                  )}
                </div>

                <div>
                  <textarea
                    value={formData.comment}
                    onChange={(e) => setFormData({...formData, comment: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    rows={2}
                    placeholder="💬 Commentaire (optionnel)"
                    maxLength={200}
                  />
                </div>
              </div>

            <div className="space-y-2">
              <div>
                <input
                  type="text"
                  value={formData.submitterName}
                  onChange={(e) => setFormData({...formData, submitterName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="👤 Votre nom *"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <input
                    type="tel"
                    value={formData.submitterPhone}
                    onChange={(e) => setFormData({...formData, submitterPhone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="📞 Téléphone"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    value={formData.submitterEmail}
                    onChange={(e) => setFormData({...formData, submitterEmail: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="📧 Email"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-600">* Au moins un moyen de contact est requis</p>
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  resetForm();
                }}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-3 rounded-lg text-sm"
                disabled={isSubmitting}
              >
                ❌
              </button>
              <button
                type="submit"
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-3 rounded-lg disabled:opacity-50 text-sm"
                disabled={isSubmitting}
              >
                {isSubmitting ? '⏳' : '✅'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StationSuggestionModal;